import { motion, AnimatePresence } from "framer-motion";
import { 
  Users, Plus, Eye, Trash2, ChevronDown, Mail, Phone, 
  MapPin, Building2, Wifi, Volume2, Plug, Crown, ArrowUp, ArrowDown 
} from "lucide-react";

const wifiLabels = {
  excellent: "Excellent WiFi",
  good: "Good WiFi", 
  fair: "Fair WiFi",
  poor: "Poor WiFi"
};

const noiseLabels = {
  quiet: "Quiet",
  moderate: "Moderate",
  lively: "Lively",
  loud: "Loud"
};

export default function AdminOwnersTab({
  owners,
  filteredOwners,
  expandedOwners,
  setExpandedOwners,
  processing,
  setShowAddOwnerModal,
  handleDeleteOwner,
  sortConfig,
  onSort
}) {
  const SortIcon = ({ field }) => {
    if (sortConfig?.field !== field) return null;
    return sortConfig.direction === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />;
  };

  return (
    <div className="bg-white rounded-xl shadow-sm overflow-hidden">
      <div className="p-4 border-b border-gray-100 flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-gray-900 flex items-center gap-2">
            <Users className="w-5 h-5" /> Venue Owners
          </h3>
          <p className="text-sm text-gray-500 mt-1">Manage claimed venue owners and their venue subscriptions</p>
        </div>
        <div className="flex items-center gap-3">
          {/* Sort controls */}
          <div className="flex items-center gap-2 text-sm">
            <span className="text-gray-500">Sort:</span>
            <button 
              onClick={() => onSort('name')}
              className={`px-2 py-1 rounded flex items-center gap-1 ${sortConfig?.field === 'name' ? 'bg-brand/10 text-brand' : 'text-gray-600 hover:bg-gray-100'}`}
            >
              Name <SortIcon field="name" />
            </button>
            <button 
              onClick={() => onSort('venue_count')}
              className={`px-2 py-1 rounded flex items-center gap-1 ${sortConfig?.field === 'venue_count' ? 'bg-brand/10 text-brand' : 'text-gray-600 hover:bg-gray-100'}`}
            >
              Venues <SortIcon field="venue_count" />
            </button>
            <button 
              onClick={() => onSort('created_at')}
              className={`px-2 py-1 rounded flex items-center gap-1 ${sortConfig?.field === 'created_at' ? 'bg-brand/10 text-brand' : 'text-gray-600 hover:bg-gray-100'}`}
            >
              Date <SortIcon field="created_at" />
            </button>
          </div>
          <button
            onClick={() => setShowAddOwnerModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-brand text-white rounded-lg hover:bg-brand-dark transition-colors"
            data-testid="add-owner-btn"
          >
            <Plus className="w-4 h-4" />
            Add Owner
          </button>
        </div>
      </div>
      <div className="divide-y divide-gray-100">
        {filteredOwners.map((owner) => (
          <div key={owner.id} className="bg-white">
            {/* Owner Header Row - Clickable to expand */}
            <div 
              className={`px-4 py-3 flex items-center justify-between cursor-pointer hover:bg-gray-50 transition-colors ${expandedOwners[owner.id] ? 'bg-gray-50' : ''}`}
              onClick={() => setExpandedOwners(prev => ({ ...prev, [owner.id]: !prev[owner.id] }))}
              data-testid={`owner-row-${owner.id}`}
            >
              <div className="flex items-center gap-4 flex-1">
                {/* Expand/Collapse Icon */}
                <div className={`transition-transform duration-200 ${expandedOwners[owner.id] ? 'rotate-90' : ''}`}>
                  <ChevronDown className="w-5 h-5 text-gray-400" />
                </div>
                
                {/* Owner Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-medium text-gray-900">{owner.name}</p>
                    {owner.email_verified && (
                      <span className="px-1.5 py-0.5 bg-green-100 text-green-700 rounded text-xs">Verified</span>
                    )}
                  </div>
                  <div className="flex items-center gap-3 mt-0.5">
                    <p className="text-sm text-gray-500 flex items-center gap-1">
                      <Mail className="w-3 h-3" />
                      {owner.email}
                    </p>
                    {owner.phone && (
                      <p className="text-sm text-gray-500 flex items-center gap-1">
                        <Phone className="w-3 h-3" />
                        {owner.phone}
                      </p>
                    )}
                  </div>
                </div>
                
                {/* Venue Summary */}
                <div className="flex items-center gap-3">
                  <div className="text-center">
                    <p className="text-lg font-semibold text-gray-900">{owner.venue_count}</p>
                    <p className="text-xs text-gray-500">Venues</p>
                  </div>
                  {owner.premium_venue_count > 0 && (
                    <div className="text-center px-3 py-1 bg-amber-50 rounded-lg">
                      <p className="text-lg font-semibold text-amber-600">{owner.premium_venue_count}</p>
                      <p className="text-xs text-amber-600">Premium</p>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Actions */}
              <div className="flex items-center gap-1.5 ml-4" onClick={e => e.stopPropagation()}>
                <button 
                  onClick={() => window.open(`/dashboard?owner=${owner.id}`, '_blank')} 
                  className="p-2 text-brand hover:bg-brand/10 rounded-lg"
                  title="View Dashboard"
                >
                  <Eye className="w-4 h-4" />
                </button>
                <button 
                  onClick={() => handleDeleteOwner(owner.id, owner.name)}
                  disabled={processing === owner.id}
                  className="p-2 text-red-500 hover:text-white hover:bg-red-500 rounded-lg disabled:opacity-50"
                  title="Delete Owner"
                  data-testid={`delete-owner-${owner.id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
            
            {/* Expanded Venues Section */}
            <AnimatePresence>
              {expandedOwners[owner.id] && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="overflow-hidden"
                >
                  <div className="bg-gray-50 border-t border-gray-200">
                    {owner.venues && owner.venues.length > 0 ? (
                      <div className="divide-y divide-gray-200">
                        {owner.venues.map((venue) => (
                          <div key={venue.id} className="px-6 py-4 ml-8 flex items-start gap-4 hover:bg-gray-100 transition-colors">
                            {/* Venue Image */}
                            <div className="w-20 h-20 rounded-lg overflow-hidden flex-shrink-0 bg-gray-200">
                              {(venue.images && venue.images.length > 0) || (venue.photos && venue.photos.length > 0) ? (
                                <img src={venue.images?.[0] || venue.photos?.[0]} alt={venue.name} className="w-full h-full object-cover" />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center text-gray-400">
                                  <MapPin className="w-8 h-8" />
                                </div>
                              )}
                            </div>
                            
                            {/* Venue Details */}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <h4 className="font-medium text-gray-900">{venue.name}</h4>
                                {venue.is_premium ? (
                                  <span className="px-2 py-0.5 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded text-xs font-medium flex items-center gap-1">
                                    <Crown className="w-3 h-3" /> Premium
                                  </span>
                                ) : (
                                  <span className="px-2 py-0.5 bg-gray-200 text-gray-600 rounded text-xs">Free</span>
                                )}
                                {venue.is_verified && (
                                  <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs">Verified</span>
                                )}
                              </div>
                              
                              <p className="text-sm text-gray-500 mt-1">
                                {venue.location?.address || venue.address}, {venue.location?.city || venue.city}
                              </p>
                              
                              {/* Venue Stats Row */}
                              <div className="flex items-center gap-4 mt-2 text-xs text-gray-600 flex-wrap">
                                <span className="flex items-center gap-1">
                                  <Building2 className="w-3 h-3" />
                                  {venue.venue_type || 'Venue'}
                                </span>
                                {(venue.amenities?.wifi_quality || venue.wifi_quality) && (
                                  <span className="flex items-center gap-1">
                                    <Wifi className="w-3 h-3" />
                                    {wifiLabels[venue.amenities?.wifi_quality || venue.wifi_quality] || venue.amenities?.wifi_quality || venue.wifi_quality}
                                  </span>
                                )}
                                {(venue.amenities?.noise_level || venue.noise_level) && (
                                  <span className="flex items-center gap-1">
                                    <Volume2 className="w-3 h-3" />
                                    {noiseLabels[venue.amenities?.noise_level || venue.noise_level] || venue.amenities?.noise_level || venue.noise_level}
                                  </span>
                                )}
                                {(venue.amenities?.power_outlets || venue.power_outlets) > 0 && (
                                  <span className="flex items-center gap-1">
                                    <Plug className="w-3 h-3" />
                                    {venue.amenities?.power_outlets || venue.power_outlets} outlets
                                  </span>
                                )}
                              </div>
                            </div>
                            
                            {/* Actions */}
                            <div className="flex items-center gap-1 shrink-0">
                              <a
                                href={`/venue/${venue.id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="p-2 text-brand hover:bg-brand/10 rounded-lg"
                                title="View Venue"
                              >
                                <Eye className="w-4 h-4" />
                              </a>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="px-6 py-8 ml-8 text-center text-gray-500">
                        <Building2 className="w-8 h-8 mx-auto mb-2 opacity-50" />
                        <p>No venues claimed by this owner</p>
                      </div>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        ))}
      </div>
      {owners.length === 0 && <div className="p-8 text-center text-gray-500">No venue owners yet</div>}
    </div>
  );
}
